/**
 * MXlab — Compatibilité en fiche produit : le bandeau et le sélecteur de découpe.
 *
 * Deux éléments personnalisés, un seul module :
 *   <mx-compat>   le bandeau à quatre états      (blocks/mx-compat.liquid)
 *   <mx-gabarit>  les vignettes de découpe       (blocks/mx-gabarit.liquid)
 *
 * Les deux blocs chargent ce module ; un module ES est indexé par son URL et n'est évalué
 * qu'une fois par document, donc l'un peut vivre sans l'autre.
 *
 * Les fonctions de stockage sont DUPLIQUÉES depuis mx-vehicle.js, à dessein : aucune entrée
 * n'est ajoutée à l'import map `@theme/*` de snippets/scripts.liquid, qui est un fichier
 * natif (règle 1 de CLAUDE.md).
 *
 * ── CE MODULE NE COMPOSE AUCUNE PHRASE. Il remplit des gabarits à trous rendus par Liquid
 * depuis les réglages du bloc. Traduire le bloc suffit donc à traduire le bandeau, et une
 * langue qui s'ouvre n'a aucune ligne de JS à toucher.
 *
 * ── IL NE SAIT PAS CE QU'IL COMPARE, et c'est le point. Il parcourt les clés que le
 * PRODUIT déclare et lit la clé de même nom sur l'équipement. Ajouter un axe — diamètre de
 * disque, de guidon — est une saisie de données : un champ de plus sur `equipement`, une
 * clé de plus dans `compat_attributs`. Zéro ligne ici.
 */

/* Clé, schéma et nom de propriété sont des LITTÉRAUX, jamais des réglages. Un réglage
   `type: "text"` matérialisé dans une template JSON devient traduisible par Translate &
   Adapt : la clé serait traduite sur les parcours non francophones et la donnée deviendrait
   introuvable. Même arbitrage que `_custom-model-id` dans mx-customlab.js.

   ⚠️⚠️ CES DEUX LIGNES SONT DUPLIQUÉES DANS `assets/mx-vehicle.js`, ET LES DEUX MODULES
   LISENT LE MÊME STOCKAGE. La duplication est voulue — aucune entrée ajoutée à l'import
   map native (règle 1 de CLAUDE.md) — mais rien dans le code ne force à les changer
   ensemble. Bumper `SCHEMA` d'un seul côté ne casse RIEN de visible : le module resté en
   arrière juge simplement le contenu invalide et rend `null`, donc le bandeau retombe en
   « dis-nous ta machine » alors que la pastille affiche toujours le véhicule, sans erreur
   ni journal. Toute modification de `STORE_KEY`, de `SCHEMA` ou de la FORME de l'objet
   stocké (`{ gabarit, v }`) se répercute dans `assets/mx-vehicle.js`, au même endroit,
   dans le même commit. */
const STORE_KEY = 'mxlab_vehicule';
const SCHEMA = 1;
const EVENT = 'mx:vehicle-changed';
const OPEN_EVENT = 'mx:vehicle-open';
const VARIANT_EVENT = 'shopify:product:select';
// Diffusé par MxGabarit à chaque choix de découpe (ou sa levée) : mx-summary.js (récap
// flottant) l'écoute pour composer la ligne « bande — découpe » du point 6, sans jamais
// recalculer de prix — celui-ci vient déjà de Liquid.
const DECOUPE_EVENT = 'mx:decoupe-changed';

/* ------------------------------------------------------------------- utilitaires */

function lsGet(k) {
  try {
    return window.localStorage.getItem(k);
  } catch (e) {
    return null; // navigation privée / stockage refusé : on continue sans persistance
  }
}

/**
 * Le véhicule mémorisé, ou null. `v` est un numéro de SCHÉMA : absent ou différent, on
 * ignore et on repart de zéro plutôt que de deviner une migration.
 */
function lireStockage() {
  const brut = lsGet(STORE_KEY);
  if (!brut) return null;
  let o = null;
  try {
    o = JSON.parse(brut);
  } catch (e) {
    return null;
  }
  if (!o || o.v !== SCHEMA || typeof o.gabarit !== 'string' || !o.gabarit) return null;
  return o.gabarit;
}

/** Lit un document JSON déposé par Liquid. Illisible => null, et l'appelant se met en retrait. */
function lireJson(el) {
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    return null;
  }
}

/**
 * L'adresse de la page qui porte le sélecteur, réglée sur le dictionnaire
 * (`mx-vehicle-lookup`, pied de page) — présent sur toutes les pages, donc toujours lu ici
 * avec succès. Liquid ne peut pas la lire directement dans `blocks/mx-compat.liquid` :
 * deux sections sans lien de parenté ; c'est ce lecteur, DUPLIQUÉ depuis la fonction `dict`
 * de `assets/mx-vehicle.js` (même arbitrage que le stockage, voir plus haut), qui fait le
 * pont. Absent (dictionnaire retiré du pied de page) => `null`, l'appelant garde alors le
 * repli Liquid déjà rendu (page d'accueil).
 */
function adresseSelecteur() {
  const el = document.querySelector('script[type="application/json"][data-mx-vehicle-lookup]');
  if (!el) return null;
  try {
    const o = JSON.parse(el.textContent);
    return (o && o.url) || null;
  } catch (e) {
    return null;
  }
}

function vider(el) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/*
 * « Quand tout le monde écoute ».
 *
 * ⚠️ MESURÉ ICI : présélectionner la bande revient à CLIQUER une option native, ce qui émet
 * `shopify:product:select`. Fait pendant l'exécution du module, ce clic partait AVANT que
 * les blocs rendus plus bas dans le DOM (récapitulatif, options, barre d'achat) n'aient posé
 * leurs écouteurs : le prix d'en-tête suivait, le récapitulatif restait sur l'ancienne bande
 * et l'ancien total. Aucune erreur, juste un panneau qui contredit la page.
 *
 * `DOMContentLoaded` se déclenche APRÈS l'exécution de TOUS les scripts différés, donc après
 * l'enregistrement de tous les écouteurs. PAS `requestAnimationFrame` : dans un onglet qui
 * n'est pas rendu (ouvert en arrière-plan, session restaurée) le rappel n'est jamais appelé
 * — le même piège que l'émission initiale du lot 1.
 */
let domPret = document.readyState === 'complete';
if (!domPret) {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      domPret = true;
    },
    { once: true }
  );
}

function quandPret(fn) {
  if (domPret) {
    setTimeout(fn, 0);
    return;
  }
  document.addEventListener('DOMContentLoaded', () => setTimeout(fn, 0), { once: true });
}

/**
 * Égalité de deux valeurs d'attribut.
 *
 * Un nombre se compare EN NOMBRE : `21` et `"21"` doivent correspondre, parce qu'un champ
 * `number_integer` sort un nombre tandis qu'un json de métachamp sort la même donnée en
 * chaîne. Sinon la comparaison échouerait en silence sur tout axe numérique — un diamètre
 * de jante, par exemple. Les références de métaobjet, elles, se comparent par HANDLE (le
 * GID n'arrive jamais jusqu'ici, `mx-vehicle-attrs` ne sort que des handles).
 */
function memeValeur(a, b) {
  if (a === undefined || a === null || b === undefined || b === null) return false;
  const na = Number(a);
  const nb = Number(b);
  if (a !== '' && b !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  return String(a) === String(b);
}

/**
 * Bande de cylindrée. Une bande VIDE d'un côté ou de l'autre ne restreint rien.
 *
 * ⚠️ C'est une règle de justesse, pas de confort, et elle marche dans les DEUX sens :
 *   · les trois équipements de la RM-Z n'ont pas de bande alors que le gabarit porte
 *     « 125-450 » — sans cette règle ils seraient tous écartés et une machine parfaitement
 *     documentée retomberait en « dis-nous ta machine » ;
 *   · à l'inverse, la KX 65 ne doit PAS hériter de l'équipement 125-450 (qui porte, lui,
 *     une fourche) : là c'est la bande qui écarte, et sans elle le bandeau annoncerait
 *     « compatible » à un client dont la machine ne l'est pas.
 */
function bandeCompatible(a, b) {
  if (a === undefined || a === null || a === '') return true;
  if (b === undefined || b === null || b === '') return true;
  return memeValeur(a, b);
}

/** Deux plages d'années se recoupent-elles ? `null` = borne ouverte (encore produit). */
function recoupe(e, g) {
  if (e.d !== null && g.f !== null && e.d > g.f) return false;
  if (g.d !== null && e.f !== null && g.d > e.f) return false;
  return true;
}

function borneHaute(a, b) {
  if (a === null || a === undefined) return b;
  if (b === null || b === undefined) return a;
  return Math.max(a, b);
}

function borneBasse(a, b) {
  if (a === null || a === undefined) return b;
  if (b === null || b === undefined) return a;
  return Math.min(a, b);
}

function remplir(gabarit, trous) {
  let out = String(gabarit || '');
  Object.keys(trous).forEach((k) => {
    out = out.split('{' + k + '}').join(trous[k] === null || trous[k] === undefined ? '' : trous[k]);
  });
  return out;
}

/**
 * MOBILE (voir mx-compat.liquid, .mx-cp__sub--mobile) : la phrase rouvre en gras le mot
 * qu'affiche le titre en desktop — jamais un second mot inventé. Si la phrase le reprend
 * déjà en tête (cas par défaut de l'état OK, « Compatible avec ta… »), on retire ce
 * doublon plutôt que de le répéter deux fois dans la même ligne. Sinon (ex. l'état KO, où
 * la phrase ne commence pas par son mot court), rien n'est retiré : le mot s'ajoute
 * devant, jamais perdu.
 */
function sansDoublon(phrase, mot) {
  const p = phrase || '';
  const m = mot || '';
  if (!p || !m) return p;
  if (p === m) return '';
  if (p.indexOf(m) === 0) return p.slice(m.length).replace(/^\s+/, '');
  return p;
}

/* ==================================================================== RÉSOLUTION */

/**
 * L'arbre de décision, au complet. Renvoie toujours un état, jamais une exception.
 *
 * ⚠️ UNE CARACTÉRISTIQUE ABSENTE EST UNE INCONNUE, JAMAIS UNE DIFFÉRENCE.
 * Un équipement qui ne renseigne aucune des clés demandées est ignoré ; si aucun n'en
 * renseigne, on est en état 1 (« dis-nous ta machine »), PAS en état 4. Dire « ça ne va
 * pas » à un client dont la machine va est le seul mode de défaillance qui coûte une vente.
 *
 * Un écart assumé avec l'arbre de la spec : quand PLUSIEURS périodes recoupent et qu'elles
 * correspondent TOUTES, on rend un oui ferme (état 2) plutôt qu'un « compatible si » qui
 * ferait douter sans raison. Le conditionnel est réservé aux cas où une partie seulement
 * des périodes correspond.
 */
function resoudre(prod, ref, handle) {
  if (!handle) return { etat: 1 };

  const g = ref.gabarits[handle];
  // Gabarit inconnu (modèle retiré, entrée supprimée) : oubli silencieux, retour à l'état 1.
  if (!g) return { etat: 1 };

  const modele = ref.modeles[g.m] || null;
  const base = { modele: modele, gabarit: g };

  const variantes = prod.variantes || [];
  if (variantes.length) {
    if (variantes.indexOf(handle) !== -1) return Object.assign({ etat: 2 }, base);
    // Compatibilité par gabarit : pas d'attribut, donc pas de raison à composer.
    return Object.assign({ etat: 4 }, base);
  }

  const cles = Object.keys(prod.attrs || {});
  if (!cles.length) return { etat: 1 };

  const candidats = (ref.equipements || []).filter(
    (e) => e.m === g.m && bandeCompatible(e.b, g.b) && recoupe(e, g)
  );
  const connus = candidats.filter((e) => e.a && cles.some((k) => e.a[k] !== undefined));
  if (!connus.length) return Object.assign({ etat: 1 }, base);

  const ok = connus.filter((e) =>
    cles.every((k) => e.a[k] === undefined || memeValeur(e.a[k], prod.attrs[k]))
  );

  if (!ok.length) return Object.assign({ etat: 4, connus: connus }, base);
  if (ok.length === connus.length) return Object.assign({ etat: 2 }, base);
  return Object.assign({ etat: 3, ok: ok, connus: connus }, base);
}

/* ====================================================================== BANDEAU */

class MxCompat extends HTMLElement {
  connectedCallback() {
    if (this.pret) return;

    this.prod = lireJson(this.querySelector('[data-mx-compat-produit]'));
    this.ref = lireJson(this.querySelector('[data-mx-compat-ref]'));
    this.t = lireJson(this.querySelector('[data-mx-compat-t]'));
    // Sans données, le bandeau garde l'état 1 rendu par Liquid : c'est le bon repli, il dit
    // déjà ce que le produit couvre.
    if (!this.prod || !this.ref || !this.t) return;

    // La vraie adresse remplace le repli « page d'accueil » rendu par Liquid, dès qu'elle
    // est lisible. Voir `adresseSelecteur` : Liquid n'a aucun moyen de la lire lui-même ici.
    const url = adresseSelecteur();
    if (url) this.t.url = url;

    this.un = this.querySelector('[data-mx-cp-un]');
    this.zone = this.querySelector('[data-mx-cp-js]');
    if (!this.un || !this.zone) return;

    this.onSync = this.onSync.bind(this);
    document.addEventListener(EVENT, this.onSync);

    // Le bouton statique (état neutre, rendu par Liquid) suit le même contrat que ceux
    // construits en JS : voir `ouvrirAuClic`. Son `href` littéral suit la même adresse
    // corrigée, pour rester juste même quand `ouvrirAuClic` ne peut pas intercepter le
    // clic (aucune pastille sur la page).
    const action = this.querySelector('[data-mx-cp-open]');
    if (action) {
      if (url) action.href = url;
      this.ouvrirAuClic(action);
    }

    this.pret = true;
    this.peindre();
  }

  /**
   * Un lien « ouvrir le sélecteur » est un VRAI lien vers la page du picker : sans JS il
   * navigue. Quand le tiroir existe sur la page (`mx-vehicle-drawer`, rendu par
   * `mx-vehicle-lookup` en pied de page — donc présent presque partout), on le préfère : le
   * client ne quitte pas la fiche. Partagé par le bouton statique (état neutre) et par
   * `.mx-cp__change` (construit en JS, peint à chaque état à machine mémorisée) : même
   * comportement, jamais deux implémentations qui pourraient diverger.
   */
  ouvrirAuClic(el) {
    el.addEventListener('click', (ev) => {
      if (!document.querySelector('mx-vehicle-drawer')) return;
      ev.preventDefault();
      document.dispatchEvent(new CustomEvent(OPEN_EVENT));
    });
  }

  disconnectedCallback() {
    if (this.onSync) document.removeEventListener(EVENT, this.onSync);
  }

  onSync() {
    this.peindre();
  }

  /* ------------------------------------------------------------------ peinture */

  peindre() {
    const r = resoudre(this.prod, this.ref, lireStockage());
    vider(this.zone);

    // ÉTAT 1, MACHINE DÉCLARÉE. Le client a répondu ; la caractéristique comparée n'est
    // simplement pas renseignée sur sa machine. Lui redemander « dis-nous ta machine »
    // serait absurde et lui ferait croire que son choix n'a pas été pris. On ne conclut
    // toujours pas — on dit juste laquelle des deux moitiés manque.
    const inconnu = r.etat === 1 && r.modele && this.t.inconnu;

    if (r.etat === 1 && !inconnu) {
      // On REVIENT à l'état 1 rendu par Liquid plutôt que de le reconstruire : c'est la même
      // phrase, déjà traduite, et le retour en arrière ne peut donc pas diverger de l'aller.
      this.zone.hidden = true;
      this.un.hidden = false;
      return;
    }

    this.un.hidden = true;
    this.zone.hidden = false;

    const modele = (r.modele && r.modele.nom) || '';

    // ÉTAT (au sens plaque) : 1 → neutre (question-mark), 2/3/4 → ok/si/ko. Carré ET
    // pictogramme suivent le MÊME nom — jamais la couleur seule.
    const nom = r.etat === 2 ? 'ok' : r.etat === 3 ? 'si' : r.etat === 4 ? 'ko' : 'inconnu';

    const carre = document.createElement('span');
    carre.className = 'mx-cp__square' + (nom === 'inconnu' ? '' : ' mx-cp__square--' + nom);
    carre.setAttribute('aria-hidden', 'true');
    const icone = this.icone(nom);
    if (icone) carre.appendChild(icone);
    this.zone.appendChild(carre);

    const texte = document.createElement('span');
    texte.className = 'mx-cp__head-text';

    const label = this.etiquette(r);

    // DESKTOP : titre à part (place disponible). MOBILE (.mx-cp__sub--mobile, voir
    // mx-compat.liquid) : pas de titre, son mot rouvre la phrase en gras à la place —
    // sans lui la règle « jamais la couleur seule » ne tiendrait plus qu'au filet, pas
    // assez pour un daltonien. Même mot (`label`), même calcul : seule la présentation
    // diffère par largeur, exactement comme pour le filet et la plaque.
    const titreEl = document.createElement('strong');
    titreEl.className = 'mx-cp__title';
    titreEl.textContent = label;
    texte.appendChild(titreEl);

    const sub1 = this.phrase(r, modele);
    if (sub1) {
      const subDesktop = document.createElement('span');
      subDesktop.className = 'mx-cp__sub mx-cp__sub--desktop';
      subDesktop.textContent = sub1;
      texte.appendChild(subDesktop);
    }

    const subMobile = document.createElement('span');
    subMobile.className = 'mx-cp__sub mx-cp__sub--mobile';
    const leadEl = document.createElement('strong');
    leadEl.className = 'mx-cp__lead-word';
    leadEl.textContent = label;
    subMobile.appendChild(leadEl);
    const reste = sansDoublon(sub1, label);
    if (reste) subMobile.appendChild(document.createTextNode(' ' + reste));
    texte.appendChild(subMobile);

    const detail = this.detail(r, modele);
    if (detail) {
      const sub = document.createElement('span');
      sub.className = 'mx-cp__sub';
      sub.textContent = detail;
      texte.appendChild(sub);
    }

    // Pas de collection résolvable => pas d'action, jamais un lien mort. Couvre aussi bien
    // le modèle sans collection que la collection non publiée : dans les deux cas Liquid
    // n'a pas d'adresse à donner.
    if (r.etat === 4 && r.modele && r.modele.url) {
      const a = document.createElement('a');
      a.className = 'mx-cp__action';
      a.href = r.modele.url;
      a.textContent = remplir(this.t.koLien, { modele: modele });
      texte.appendChild(a);
    }

    this.zone.appendChild(texte);

    // « CHANGER DE VÉHICULE » — pas cosmétique : la pastille a été retirée, ce lien est le
    // SEUL moyen de corriger une machine mal déclarée depuis la fiche. Uniquement ICI (les
    // quatre branches qui passent ce point ont toutes une machine mémorisée) — l'état
    // neutre « aucune machine déclarée » revient plus haut et ne construit jamais `texte`,
    // son propre bouton (`t_action`) porte déjà l'invitation.
    if (this.t.changer && this.t.url) {
      const changer = document.createElement('a');
      changer.className = 'mx-cp__change';
      changer.href = this.t.url;
      changer.textContent = this.t.changer;
      this.ouvrirAuClic(changer);
      this.zone.appendChild(changer);
    }
  }

  /** Clone l'icône rendue par `mx-icon` : aucun SVG n'est écrit en JS. */
  icone(nom) {
    if (!nom) return null;
    const source = this.querySelector('[data-mx-cp-icon="' + nom + '"] svg');
    if (!source) return null;
    return source.cloneNode(true);
  }

  /** Le mot court, en capitales, affiché DANS le carré de plaque. */
  etiquette(r) {
    if (r.etat === 2) return this.t.labelOk || '';
    if (r.etat === 3) return this.t.labelSi || '';
    if (r.etat === 4) return this.t.labelKo || '';
    return this.t.labelInconnu || '';
  }

  /** La phrase complète (sous-titre 1, sous la plaque) : le détail que le mot court résume. */
  phrase(r, modele) {
    // Machine déclarée mais caractéristique inconnue : on redit ce que le produit couvre,
    // exactement comme l'état 1 de Liquid — c'est la seule affirmation encore vraie.
    if (r.etat === 1) return this.t.couvre || '';
    if (r.etat === 2) {
      return remplir(this.t.ok, { modele: modele, gabarit: r.gabarit.l || '' });
    }
    if (r.etat === 3) {
      const bornes = this.bornes(r);
      return remplir(this.t.si, { modele: modele, annees: this.phraseAnnees(bornes.d, bornes.f) });
    }
    return remplir(this.t.ko, { modele: modele });
  }

  detail(r, modele) {
    if (r.etat === 1) return remplir(this.t.inconnu, { modele: modele });
    if (r.etat === 3) {
      const liste = this.liste(r.connus, r.gabarit);
      if (!liste) return '';
      return remplir(this.t.siDetail, { liste: liste });
    }
    if (r.etat === 4) {
      // ⚠️ JAMAIS de non ferme sans raison. Une compatibilité par gabarit n'en a pas à
      // donner (aucune caractéristique en jeu) : le titre suffit, et on n'invente rien.
      if (!r.connus || !r.connus.length) return '';
      const ecart = this.ecart(r.connus);
      if (!ecart) return '';
      return remplir(this.t.koRaison, { modele: modele, a: ecart, exige: this.t.exige || '' });
    }
    return '';
  }

  /** Les années du verdict conditionnel : la plage du gabarit, resserrée sur ce qui va. */
  bornes(r) {
    let d = null;
    let f = null;
    let ouvertD = false;
    let ouvertF = false;
    r.ok.forEach((e) => {
      if (e.d === null) ouvertD = true;
      else d = d === null ? e.d : Math.min(d, e.d);
      if (e.f === null) ouvertF = true;
      else f = f === null ? e.f : Math.max(f, e.f);
    });
    return {
      d: borneHaute(r.gabarit.d, ouvertD ? null : d),
      f: borneBasse(r.gabarit.f, ouvertF ? null : f),
    };
  }

  /**
   * Le VERDICT : « est de 2013 ou 2014 ». Deux années consécutives s'énumèrent, elles ne
   * se bornent pas — on demande au client de reconnaître son millésime dans une liste
   * courte, pas de situer une plage.
   */
  phraseAnnees(d, f) {
    if (d !== null && f !== null && f - d === 1) return remplir(this.t.ou, { debut: d, fin: f });
    return this.phrasePeriode(d, f);
  }

  /**
   * Une PÉRIODE de la liste explicative : toujours bornée, jamais énumérée. « Showa 48 SFF
   * 2013 à 2014 » se lit comme une période ; « Showa 48 SFF 2013 ou 2014 » se lirait comme
   * une hésitation sur la fourche elle-même.
   */
  phrasePeriode(d, f) {
    if (d === null && f === null) return '';
    if (d === null) return remplir(this.t.jusqu, { fin: f });
    if (f === null) return remplir(this.t.depuis, { debut: d });
    if (d === f) return String(d);
    return remplir(this.t.plage, { debut: d, fin: f });
  }

  /**
   * Les périodes qui expliquent l'ambiguïté, dans l'ordre : « Showa 47 mm jusqu'en 2012,
   * Showa 48 SFF 2013 à 2014, Showa 49 depuis 2015 ». La PREMIÈRE se dit « jusqu'en »
   * plutôt qu'avec sa borne basse : le client n'a que faire de savoir depuis quand une
   * fourche existait avant la période qui l'intéresse.
   */
  liste(equipements, gabarit) {
    const tries = equipements.slice().sort((a, b) => {
      const ad = a.d === null ? -Infinity : a.d;
      const bd = b.d === null ? -Infinity : b.d;
      return ad - bd;
    });

    const morceaux = [];
    tries.forEach((e, i) => {
      const label = this.valeurs(e);
      if (!label) return;
      let annees;
      if (i === 0 && e.f !== null && (gabarit.d === null || e.d === null || e.d <= gabarit.d)) {
        annees = remplir(this.t.jusqu, { fin: e.f });
      } else {
        annees = this.phrasePeriode(e.d, e.f);
      }
      morceaux.push(annees ? label + ' ' + annees : label);
    });

    return morceaux.join(this.t.sep || ', ');
  }

  /** Les valeurs connues d'un équipement, en libellés lisibles. */
  valeurs(e) {
    const cles = Object.keys(this.prod.attrs || {});
    const out = [];
    cles.forEach((k) => {
      if (e.a[k] === undefined) return;
      out.push(this.libelle(k, e.a[k]));
    });
    return out.join(' · ');
  }

  /** Un handle ne se montre pas à un client. Sans libellé connu, le code brut : jamais faux. */
  libelle(cle, valeur) {
    const table = (this.ref.labels || {})[cle];
    if (table && table[valeur] !== undefined) return table[valeur];
    return String(valeur);
  }

  /** Ce que la machine a, là où ça diffère de ce que le produit exige. */
  ecart(connus) {
    const cles = Object.keys(this.prod.attrs || {});
    for (let i = 0; i < cles.length; i += 1) {
      const k = cles[i];
      const vus = [];
      connus.forEach((e) => {
        if (e.a[k] === undefined || memeValeur(e.a[k], this.prod.attrs[k])) return;
        const lab = this.libelle(k, e.a[k]);
        if (vus.indexOf(lab) === -1) vus.push(lab);
      });
      if (vus.length) return vus.join(this.t.sep || ', ');
    }
    return '';
  }
}

/* ============================================================ SÉLECTEUR DE DÉCOUPE */

class MxGabarit extends HTMLElement {
  connectedCallback() {
    if (this.pret) return;

    this.tuiles = [...this.querySelectorAll('[data-mx-gab-tile]')];
    if (!this.tuiles.length) return;

    this.cle = this.querySelector('[data-mx-gab-cle]');
    this.vide = this.querySelector('[data-mx-gab-vide]');
    this.bandes = lireJson(this.querySelector('[data-mx-gab-bandes]')) || {};

    // Le rappel de choix vit dans l'EN-TÊTE de l'étape, qui n'appartient plus à ce bloc :
    // la découpe n'est pas une étape, c'est un champ de « Ta moto ». On remonte à l'étape
    // par son attribut, jamais par un nom de conteneur du thème.
    const etape = this.closest('[data-mx-step]');
    this.valeur = etape ? etape.querySelector('[data-mx-gab-value]') : null;

    this.memo = lireStockage();
    this.aPreselectionner = true;
    this.bascule = false;
    // Une URL portant déjà `variant` est un choix EXPLICITE (lien partagé, retour d'une
    // recherche) : on ne le remplace pas par une déduction, même juste.
    this.varianteImposee = false;
    try {
      this.varianteImposee = new URLSearchParams(window.location.search).has('variant');
    } catch (e) {
      /* URL illisible : on se comporte comme si rien n'était imposé */
    }

    this.onChoix = this.onChoix.bind(this);
    this.onVariante = this.onVariante.bind(this);
    this.onVehicule = this.onVehicule.bind(this);

    this.addEventListener('change', this.onChoix);
    document.addEventListener(VARIANT_EVENT, this.onVariante);
    document.addEventListener(EVENT, this.onVehicule);

    this.pret = true;
    this.filtrer();
  }

  disconnectedCallback() {
    document.removeEventListener(VARIANT_EVENT, this.onVariante);
    document.removeEventListener(EVENT, this.onVehicule);
  }

  /* ------------------------------------------------------------------- la bande */

  /**
   * Le PÉRIMÈTRE du produit courant : le plus proche ancêtre qui déclare un produit.
   *
   * ⚠️ NE JAMAIS INTERROGER LE DOCUMENT pour lire ou cliquer une valeur d'option. C'est
   * juste tant qu'un seul produit est dans la page, et faux dès qu'un second y entre —
   * modale d'ajout rapide, carte de recommandation, tiroir de panier. Le sélecteur de
   * découpe lirait alors la cylindrée d'une AUTRE machine, ou irait cocher la sienne, en
   * silence. Le fait que les identifiants de `this.bandes` soient propres à ce produit ne
   * protège pas : deux instances du MÊME produit dans la page partagent ces identifiants.
   *
   * On ne nomme aucun élément d'architecture du thème : on remonte par `closest()` vers ce
   * qui DÉCLARE un produit, pas vers un conteneur connu. N'importe quel porteur ferait
   * l'affaire du moment qu'il porte l'attribut.
   *
   * Aucun périmètre => on ne filtre pas (toutes les découpes s'affichent). C'est l'état de
   * repli du contrat de robustesse, et `signalerMapping` le fait remonter en console.
   */
  perimetre() {
    return this.closest('[data-product-id]');
  }

  /**
   * La bande de cylindrée COURANTE, lue sur les options natives DU PRODUIT COURANT.
   *
   * `[data-option-value-id]:checked` attrape aussi bien un `input[type=radio]` qu'une
   * `option` sélectionnée, donc la lecture survit à un changement de présentation du
   * sélecteur de variantes. La correspondance passe par l'IDENTIFIANT de valeur d'option,
   * jamais par son libellé, qui est traduit.
   */
  bandeCourante() {
    const zone = this.perimetre();
    if (!zone) return null;
    const coches = zone.querySelectorAll('[data-option-value-id]:checked');
    for (const el of coches) {
      const id = el.dataset ? el.dataset.optionValueId : null;
      if (id && this.bandes[id] !== undefined) return String(this.bandes[id]);
    }
    return null;
  }

  /**
   * Bascule l'option native vers la bande voulue, en la CLIQUANT : aucun re-rendu maison.
   * Le clic reste enfermé dans le périmètre du produit courant — cliquer l'option d'un
   * autre produit changerait SA variante, donc son prix, depuis cette fiche.
   */
  basculerVers(bande) {
    const zone = this.perimetre();
    if (!zone) return false;
    const ids = Object.keys(this.bandes).filter((id) => String(this.bandes[id]) === String(bande));
    for (const id of ids) {
      const el = zone.querySelector('[data-option-value-id="' + id + '"]');
      if (el && !el.checked && !el.disabled) {
        el.click();
        return true;
      }
    }
    return false;
  }

  onVariante(event) {
    // GARDE DE SIGNATURE : un `shopify:product:select` émis par une app tierce peut arriver
    // sans promesse, et `.then()` lèverait alors un TypeError.
    if (typeof event?.promise?.then !== 'function') {
      this.filtrer();
      return;
    }
    // L'événement part AVANT le fetch : la valeur cochée n'est à jour qu'une fois la
    // promesse résolue, donc après le morphing du sélecteur natif.
    event.promise.then(() => this.filtrer()).catch(() => {});
  }

  onVehicule(event) {
    const suivant = (event && event.detail && event.detail.gabarit) || lireStockage();
    if (suivant === this.memo) return;
    this.memo = suivant;
    // Le client vient de déclarer sa machine : c'est le moment où la présélection paye.
    this.aPreselectionner = true;
    this.bascule = false;
    this.filtrer();
  }

  /* ------------------------------------------------------------------ filtrage */

  handleDe(tuile) {
    const input = tuile.querySelector('input');
    return input ? input.dataset.mxGabHandle : null;
  }

  bandeDe(tuile) {
    const input = tuile.querySelector('input');
    const b = input ? input.dataset.mxGabBande : '';
    return b === undefined || b === null || b === '' ? null : String(b);
  }

  filtrer() {
    const bande = this.bandeCourante();
    const visibles = [];

    this.tuiles.forEach((tuile) => {
      const b = this.bandeDe(tuile);
      // Bande courante inconnue (produit sans option de cylindrée) ou gabarit sans bande :
      // on montre. Une inconnue ne masque rien — c'est la même règle que côté bandeau.
      const garder = bande === null || b === null || b === bande;
      tuile.hidden = !garder;
      const input = tuile.querySelector('input');
      if (!garder && input && input.checked) {
        // Changement de bande : la sélection qui ne correspond plus TOMBE. Une vignette
        // cochée mais masquée enverrait au panier une découpe que le client ne voit pas.
        input.checked = false;
      }
      if (garder) visibles.push(tuile);
    });

    this.signalerMapping(bande);

    if (this.vide) this.vide.hidden = visibles.length > 0;
    this.preselectionner(bande, visibles);
    this.onChoix();
  }

  /**
   * `cylindree_par_option` absent ou périmé sur un produit qui couvre PLUSIEURS bandes :
   * on ne filtre pas (toutes les découpes s'affichent) et on le signale en console. Ne
   * jamais casser : une grille complète reste utilisable, une grille vide non.
   * Le mapping est à régénérer après toute modification des valeurs d'option — dupliquer
   * un produit crée de nouveaux identifiants, donc un mapping hérité pointe dans le vide.
   */
  signalerMapping(bande) {
    if (bande !== null || this.signale) return;
    const distinctes = new Set(this.tuiles.map((t) => this.bandeDe(t)).filter((b) => b !== null));
    if (distinctes.size < 2) return; // produit à une seule bande : rien à filtrer, rien à dire
    this.signale = true;
    console.warn(
      '[mx-gabarit] Aucune bande de cylindrée résolue : mxlab.cylindree_par_option est absent ou périmé. Toutes les découpes sont affichées.'
    );
  }

  /**
   * Présélection : par machine mémorisée compatible en priorité, sinon par la bande la
   * plus chère (lot 2 — jamais celle que Shopify présélectionne par défaut, la moins
   * chère).
   *
   * ⚠️ Un gabarit UNIQUE n'est PAS présélectionné, contrairement au sélecteur du lot 1.
   * Là le client s'identifie ; ici il commande une découpe, et sa case cochée est ce qui
   * protège contre un bon à tirer produit sur la mauvaise machine.
   */
  preselectionner(bande, visibles) {
    if (!this.aPreselectionner) return;

    const cible = this.memo ? this.tuiles.find((t) => this.handleDe(t) === this.memo) : null;

    if (!cible) {
      // Pas de machine mémorisée, ou mémorisée hors de ce produit : repli PAR PRIX.
      this.preselectionnerSansMemo(bande);
      return;
    }

    if (visibles.indexOf(cible) === -1) {
      // La découpe existe mais dans une autre bande : on demande la bascule à l'option
      // NATIVE, une seule fois, et la présélection reprendra quand elle sera revenue.
      const b = this.bandeDe(cible);
      if (!this.bascule && !this.varianteImposee && b !== null && bande !== null) {
        this.bascule = true;
        // Différée : cliquer l'option émet un événement de variante que les blocs voisins
        // doivent pouvoir entendre (cf. `quandPret`). La présélection reprendra toute seule
        // quand la nouvelle bande sera revenue.
        quandPret(() => this.basculerVers(b));
        return;
      }
      this.aPreselectionner = false;
      return;
    }

    const input = cible.querySelector('input');
    if (input && !input.checked) input.checked = true;
    this.aPreselectionner = false;
  }

  /**
   * Repli PAR PRIX (lot 2) : coche la bande la plus chère quand aucune machine mémorisée
   * ne s'applique à ce produit. Le prix vient des tuiles de cylindrée elles-mêmes
   * (`data-price`, posé par `snippets/mx-tuile-option.liquid`) — aucune donnée écrite ici.
   * Mêmes protections que la bascule par véhicule : une URL portant déjà `?variant=` n'est
   * jamais écrasée, une seule bascule par chargement (`this.bascule`, partagé avec l'autre
   * repli). Un produit sans option de cylindrée (`bande === null`) n'a rien à basculer.
   */
  preselectionnerSansMemo(bande) {
    if (bande === null || this.varianteImposee) {
      this.aPreselectionner = false;
      return;
    }
    const plusChere = this.plusChere();
    if (!this.bascule && plusChere !== null && plusChere !== bande) {
      this.bascule = true;
      quandPret(() => this.basculerVers(plusChere));
      return;
    }
    this.aPreselectionner = false;
  }

  /** La bande dont le prix plancher (le moins cher de ses variantes) est le plus haut. */
  plusChere() {
    const zone = this.perimetre();
    if (!zone) return null;
    let meilleure = null;
    let meilleurPrix = -Infinity;
    Object.keys(this.bandes).forEach((id) => {
      const el = zone.querySelector('[data-option-value-id="' + id + '"]');
      const prix = el ? Number(el.dataset.price) : NaN;
      if (!Number.isNaN(prix) && prix > meilleurPrix) {
        meilleurPrix = prix;
        meilleure = String(this.bandes[id]);
      }
    });
    return meilleure;
  }

  /* -------------------------------------------------------- propriétés de ligne */

  /**
   * Synchronise la propriété TECHNIQUE et le rappel d'étape.
   *
   * Le libellé traduit part nativement : c'est la `value` du radio. Le HANDLE, lui, ne peut
   * pas voyager dans le même champ — d'où ce champ caché, tenu à jour ici. Sans JS, la
   * commande porte donc le libellé mais pas le handle : c'est signalé dans le schéma du bloc.
   *
   * The step subtitle shows the radio `value` as is: it already holds the full label
   * "{modele}, {gabarit}" (snippets/mx-decoupe.liquid), so adding the model would repeat
   * it. Until a découpe is picked, the subtitle falls back to the band alone (`data-mx-cyl`,
   * server-rendered, so right without JS and refreshed by morphing), or nothing when no
   * band is picked either (product without a cylindrée option, e.g. the Kove).
   *
   * Diffuse aussi `DECOUPE_EVENT` (point 6) : le récapitulatif flottant (mx-summary.js) n'a
   * aucun autre moyen de connaître la découpe, propriété de ligne jamais rendue côté
   * serveur. Aucun prix dans l'événement — celui-ci reste calculé et affiché par Liquid,
   * mx-summary.js ne fait que compléter le libellé déjà en place.
   */
  onChoix() {
    const input = this.tuiles
      .map((t) => t.querySelector('input'))
      .find((i) => i && i.checked && !i.closest('[data-mx-gab-tile]').hidden);

    if (this.cle) this.cle.value = input ? input.dataset.mxGabHandle || '' : '';

    let cylindree = '';
    if (this.valeur) {
      cylindree = this.valeur.dataset.mxCyl || '';
      const texte = input && input.value ? input.value : cylindree;
      this.valeur.textContent = texte;
      this.valeur.hidden = texte === '';
    }

    const zone = this.perimetre();
    document.dispatchEvent(
      new CustomEvent(DECOUPE_EVENT, {
        detail: {
          productId: zone ? zone.dataset.productId : null,
          bande: cylindree || null,
          decoupe: input ? input.dataset.mxGabNom : null,
        },
      })
    );
  }
}

/* ==================================================================== définitions */

if (!customElements.get('mx-compat')) {
  customElements.define('mx-compat', MxCompat);
}
if (!customElements.get('mx-gabarit')) {
  customElements.define('mx-gabarit', MxGabarit);
}
